/**
 * Model interfaces for the Review and Publish page
 */
import { PlanStatus } from '../../utils/constants';


export interface PrimaryGradingVolumeData {
    recommended: VolumeBreakdown;
    override: VolumeBreakdown;
}

export interface VolumeBreakdown {
    total: number;
    alps: number;
}

export interface OverrideMetrics {
    total: number;
    average: number;
}

export interface BacklogProjections {
    endOfShift: VolumeBreakdown;
    endOfWeek: VolumeBreakdown;
}

export interface ActualVsRecommended {
    actual: number;
    recommended: number;
}

export interface SimpleIntValue {
    intValue: number;
}

export interface EnhancedShiftTotals {
    headcount: ActualVsRecommended
    hours: ActualVsRecommended
    problemSolveVolume: SimpleIntValue
    amazonResaleVolume: SimpleIntValue
    vetAcceptedHours: SimpleIntValue
    vtoAcceptedHours: SimpleIntValue
    amazonReadyAcceptedHours: SimpleIntValue
    showHoursNeeded: SimpleIntValue
    // V2 (UTR-REFLEX-1163): labor-hours delta as a percentage magnitude — the
    // number shown in the Labor Hours Delta card's deficit/surplus pill. Unsigned;
    // the deficit-vs-surplus direction comes from the sign of showHoursNeeded, not
    // from this field. Optional; real backend is UTR-REFLEX-1172.
    laborHoursDeltaPercent?: number
    // V2 (UTR-REFLEX-1163): Shift Totals panel. regularScheduledHours = showHours
    // (base scheduled labor); totalAvailableHours = regular + flexible components.
    // Optional; real backend surfaces these in UTR-REFLEX-1172.
    regularScheduledHours?: SimpleIntValue
    totalAvailableHours?: SimpleIntValue
}

export interface ShiftNote {
    note: string;
}

export interface ShiftTimelineEvent {
    label: string;
    startTime: string;
    endTime?: string;
}

export interface ShiftSummaryCategory {
    heading: string;
    planOutput: ShiftSummaryData[];
}

/**
 * Whether a time bucket holds observed data or engine output (mid-shift re-plan). On a re-plan the
 * elapsed hours are ACTUAL and the remaining hours PROJECTED. Absent on Start-of-Shift plans and plans
 * written before the field existed; treat absent as PROJECTED.
 */
export enum DataKind {
    ACTUAL = "ACTUAL",
    PROJECTED = "PROJECTED",
}

export interface ShiftSummaryData {
    startTime: string;
    endTime: string;
    data: DataEntry[];
    /** ACTUAL for an elapsed hour, PROJECTED (or absent) for a planned hour. */
    dataKind?: DataKind | null;
}

export interface DataEntry {
    label: string;
    intValue: number;
    hoursRequired?: number;
}

export interface ProjectedVolumeDataPoint {
    time: string;
    recommended: number;
    /** ACTUAL for an elapsed hour, PROJECTED (or absent) for a planned hour. */
    dataKind?: DataKind | null;
}

export interface GetShiftPlanOutputRequest {
    planId: string;
    date: string;
    warehouseId: string;
    shiftType: string;
    // V2 (UTR-REFLEX-1163): which plan view to fetch — RCC Proposed vs Operator
    // Override. Each view returns its own metrics/totals/series. Optional; the V1
    // path omits it. Real backend handling is UTR-REFLEX-1172.
    planView?: string;
}

export interface GetShiftPlanOutputResponse {
    planId: string;
    planStatus: PlanStatus;
    primaryGradingVolume: PrimaryGradingVolumeData;
    /**
     * @deprecated Use primaryGradingVolumeTrend. The backend keeps sending the identical series during
     * the UI migration; read it only through getVolumeTrend (fallback for responses without the new field).
     */
    projectedPrimaryGradingVolume: ProjectedVolumeDataPoint[];
    /**
     * Primary grading volume over time: the actual series (elapsed hours) stitched ahead of the
     * projected series (remaining hours), each point tagged ACTUAL or PROJECTED via dataKind.
     * Optional until every backend stage sends it.
     */
    primaryGradingVolumeTrend?: ProjectedVolumeDataPoint[];
    overrides: OverrideMetrics;
    // Total override count for the Operator Override plan, computed by the backend
    // (RCCPlanDWTableProcessor.populateOverrideValues) and surfaced on the plan's
    // output (UTR-REFLEX-1163). Shown in the "Current plan input summary" header.
    totalOverrides?: number;
    backlogProjections: BacklogProjections;
    shiftTotals: EnhancedShiftTotals;
    shiftNotes: ShiftNote[];
    shiftTimeline: ShiftTimelineEvent[];
    shiftSummary: ShiftSummaryCategory[];
}

export const buildGetShiftPlanOutputRequest = (
    planId: string, date: string, warehouseId: string, shiftType: string, planView?: string
): GetShiftPlanOutputRequest => {
    return {
        planId, date, warehouseId, shiftType, planView
    }
}
